import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Avatar,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useState, createElement, isValidElement } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faSignOutAlt,
  faUser,
  faCog,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Status Pill Component
 * Displays status with appropriate color coding
 */
export const StatusPill = ({ status, tone = "neutral" }) => {
  const theme = useTheme();

  const toneConfig = {
    success: {
      bg: "success.lighter",
      color: "success.dark",
    },
    warning: {
      bg: "warning.lighter",
      color: "warning.dark",
    },
    error: {
      bg: "error.lighter",
      color: "error.dark",
    },
    info: {
      bg: "info.lighter",
      color: "info.dark",
    },
    primary: {
      bg: "primary.lighter",
      color: "primary.dark",
    },
    neutral: {
      bg: "grey.200",
      color: "grey.700",
    },
  };

  const config = toneConfig[tone] || toneConfig.neutral;

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: theme.palette[config.bg.split(".")[0]][config.bg.split(".")[1]] || theme.palette[config.bg],
        color: theme.palette[config.color.split(".")[0]][config.color.split(".")[1]] || theme.palette[config.color],
        fontWeight: 400,
        fontSize: "0.875rem",
        height: "auto",
        borderRadius: "12px",
        py: 0.75,
        px: 0.5,
        letterSpacing: 0.3,
      }}
    />
  );
};

/**
 * Avatar Component
 * Displays user avatar with initials
 */
export const UserAvatar = ({ name, size = "md", src }) => {
  const sizeMap = {
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64,
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <Avatar
      src={src}
      sx={{
        width: sizeMap[size],
        height: sizeMap[size],
        backgroundColor: "primary.main",
        color: "#fff",
        fontWeight: 700,
        fontSize: size === "xl" ? "1.5rem" : size === "lg" ? "1rem" : "0.875rem",
      }}
    >
      {getInitials(name)}
    </Avatar>
  );
};

/**
 * Data Card Component
 * Reusable card for displaying data with consistent styling
 */
export const DataCard = ({ title, value, subtitle, icon, trend, color = "primary" }) => {
  const theme = useTheme();

  const getColorValue = (colorName) => {
    return theme.palette[colorName]?.main || theme.palette.primary.main;
  };

  const getBackgroundColor = (colorName) => {
    return theme.palette[colorName]?.lighter || theme.palette.primary.lighter;
  };

  return (
    <Card
      sx={{
        backgroundColor: "background.paper",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontWeight: 500, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: getColorValue(color),
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                backgroundColor: getBackgroundColor(color),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: getColorValue(color),
                marginLeft: 2,
              }}
            >
              <FontAwesomeIcon icon={icon} size="lg" />
            </Box>
          )}
        </Box>
        {trend && (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={trend}
              size="small"
              color={trend.includes("+") ? "success" : "error"}
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Header Component
 * Responsive header with user menu
 */
export const Header = ({ onMenuClick, user, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const getPrimaryColor = () => {
    return theme.palette.primary?.main || "#DC0069";
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "background.paper",
        color: "text.primary",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "12px 20px",
        }}
      >
        {/* Left Section - Menu Icon */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <FontAwesomeIcon icon={faBars} />
          </IconButton>
        )}

        {/* Center - Logo/Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: getPrimaryColor(),
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Augment HR
        </Typography>

        {/* Right Section - User Menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleMenuOpen} size="small">
            <UserAvatar name={user?.name || "User"} size="sm" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                {user?.name || "User"}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="textSecondary">
                {user?.email || "user@example.com"}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faUser} size="sm" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faCog} size="sm" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onLogout();
              }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faSignOutAlt} size="sm" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

/**
 * Sidebar Navigation Component
 * Mobile-friendly responsive sidebar
 */
export const Sidebar = ({ open, onClose, menuItems, activeItem, onItemClick }) => {
  const theme = useTheme();

  const getBackgroundColor = () => {
    return theme.palette.primary?.lighter || "rgba(220, 0, 105, 0.08)";
  };

  const getPrimaryColor = () => {
    return theme.palette.primary?.main || "#DC0069";
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 240,
          backgroundColor: "background.paper",
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: getPrimaryColor() }}>
          Augment HR
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.id}
            onClick={() => {
              onItemClick(item.id);
              onClose();
            }}
            selected={activeItem === item.id}
            sx={{
              "&.Mui-selected": {
                backgroundColor: getBackgroundColor(),
                color: getPrimaryColor(),
                "& .MuiListItemIcon-root": {
                  color: getPrimaryColor(),
                },
              },
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={item.icon} size="sm" />
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

/**
 * Section Header Component
 * Consistent section title styling
 */
export const SectionHeader = ({ title, subtitle, action }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 3,
      }}
    >
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
};

/**
 * Grid Layout Component
 * Responsive grid for cards and content
 */
export const GridLayout = ({ children, spacing = 3, columns = { xs: 1, sm: 2, md: 3, lg: 4 } }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
          ...columns,
        },
        gap: spacing,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Empty State Component
 * Display when there is no data
 */
function renderEmptyStateIcon(icon) {
  if (!icon) return null;
  if (typeof icon === "string") {
    return <FontAwesomeIcon icon={icon} size="3x" />;
  }
  if (isValidElement(icon)) {
    return icon;
  }
  if (typeof icon === "function" || (typeof icon === "object" && icon.render)) {
    return createElement(icon, { size: 48, strokeWidth: 1.5 });
  }
  return icon;
}

export const EmptyState = ({ icon, title, description, desc, action }) => {
  const theme = useTheme();
  const body = description || desc;

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 6,
        px: 2,
        backgroundColor: "background.default",
        borderRadius: 2,
        border: `2px dashed ${theme.palette.divider}`,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
            color: "text.secondary",
          }}
        >
          {renderEmptyStateIcon(icon)}
        </Box>
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: "text.primary",
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 3 }}
        >
          {body}
        </Typography>
      )}
      {action && <Box>{action}</Box>}
    </Box>
  );
};
